import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { http } from '@/shared/api/http-client'
import { ApiRoutes, isAuthRoute } from '@/shared/api/routes'
import { env } from '@/shared/config/env'
import { AuthErrorReason } from '../model/auth.errors'
import { authStore } from './auth-store'

const BUSINESS_401_REASONS: ReadonlySet<string> = new Set([
  AuthErrorReason.CURRENT_PASSWORD_MISMATCH,
])

interface RefreshResponse {
  access_token: string
  expires_in: number
}

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _skipAuth?: boolean
}

let inflight: Promise<string> | null = null
let onUnauthenticated: (() => void) | null = null

export async function refresh(): Promise<string> {
  if (inflight) return inflight

  inflight = http
    .post<RefreshResponse>(ApiRoutes.REFRESH, {}, {
      _skipAuth: true,
    } as RetriableRequestConfig)
    .then(r => {
      authStore.setAccessToken(r.data.access_token, r.data.expires_in)
      return r.data.access_token
    })
    .catch(err => {
      authStore.clear()
      authStore.clearSessionMarkers()
      throw err
    })
    .finally(() => {
      inflight = null
    })

  return inflight
}

export function setOnUnauthenticated(cb: () => void): void {
  onUnauthenticated = cb
}

function notifyUnauthenticated() {
  try {
    onUnauthenticated?.()
  } catch (err) {
    console.error('[refresh-controller] onUnauthenticated threw', err)
  }
}

let installed = false

export function installAuthInterceptors(): void {
  if (installed) return
  installed = true

  http.interceptors.request.use(config => {
    const cfg = config as RetriableRequestConfig
    if (cfg._skipAuth) return cfg

    const token = authStore.getAccessToken()
    if (token) {
      cfg.headers.set('Authorization', `Bearer ${token}`)
    }
    return cfg
  })

  http.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const original = error.config as RetriableRequestConfig | undefined
      const status = error.response?.status

      if (status !== 401 || !original) {
        return Promise.reject(error)
      }

      const reason = (error.response?.data as { reason?: string } | undefined)
        ?.reason
      if (reason && BUSINESS_401_REASONS.has(reason)) {
        return Promise.reject(error)
      }

      if (isAuthRoute(original.url)) {
        const url = original.url ?? ''
        const isLoginAttempt = url.endsWith(ApiRoutes.LOGIN)
        if (!isLoginAttempt) {
          authStore.clear()
          notifyUnauthenticated()
        }
        return Promise.reject(error)
      }

      if (!env.authRefreshEnabled) {
        authStore.clear()
        notifyUnauthenticated()
        return Promise.reject(error)
      }

      if (original._retry) {
        authStore.clear()
        notifyUnauthenticated()
        return Promise.reject(error)
      }
      original._retry = true

      try {
        await refresh()
      } catch {
        notifyUnauthenticated()
        return Promise.reject(error)
      }

      const newToken = authStore.getAccessToken()
      if (newToken) {
        original.headers.set('Authorization', `Bearer ${newToken}`)
      }
      return http(original)
    },
  )

  authStore.__registerProactiveRefresh(() => {
    refresh().catch(() => {
      notifyUnauthenticated()
    })
  })
}
